import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { CheckIcon, XMarkIcon as XMarkIconMini } from '@heroicons/react/20/solid';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMutationFill, useQueryGetTarif } from '@workspace/core-ts/api';
import { Button, LoaderScreen, Commission } from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { Casques, Depanneuse, Protection } from '@workspace/core-ts/icons-amo';
import { getStepBySlug } from '@workspace/core-ts/utils';
import { cn, normalizeEuro } from '@workspace/utils';
import { Fragment, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { FormSchemaType } from '@/schema';
import steps from './index';
import { useTarifFormatedData } from './tarification/use-tarif-formated-data';
import { useSource } from '@workspace/core-ts/hooks';

const garanties = {
  RC: {
    description: 'Prise en charge des dommages matériels et corporels causés aux tiers, y compris au passager.',
  },
  DR: {
    description:
      'Lorsqu’à la suite d’un sinistre, il y a litige avec un tiers, assistance et prise en charge des frais (avocats, expertise) jusqu’à 4.600 € HT par sinistre',
  },
  ASS1: {
    description:
      'Intervention en cas de panne, crevaison, gel ou erreur de carburant, perte-vol des clés et papiers, assistance en cas de retrait de permis, assistance psychologique, assistance à l’étranger, remorquage jusqu’à 200 €',
  },
  ASS1TSCA: {
    description:
      'Intervention en cas de panne, crevaison, gel ou erreur de carburant, perte-vol des clés et papiers, assistance en cas de retrait de permis, assistance psychologique, assistance à l’étranger, remorquage jusqu’à 200 €',
  },
  VOL: {
    description: 'En cas de vol ou tentative de vol, prise en charge des dommages, déduction faite de la franchise',
  },
  INC: {
    description: 'En cas d’incendie, prise en charge des dommages, déduction faite de la franchise',
  },
  CN: {
    description: 'En cas de catastrophes naturelles, prise en charge des dommages, déduction faite de la franchise',
  },
  CT: {
    description: 'En cas de catastrophes technologiques, prise en charge des dommages, déduction faite de la franchise',
  },
  ATT: {
    description: 'En cas d’attentat ou acte de terrorisme, prise en charge des dommages, déduction faite de la franchise',
  },
  DTA: {
    description: 'Suite à un accident, prise en charge des dommages matériels, déduction faite de la franchise',
  },
} as any;

export default function TarificationContainer() {
  const { actions, meta, state } = useGlobalForm();
  const queryTarif = useQueryGetTarif(meta.token, state);

  useEffect(() => {
    if (queryTarif.isSuccess) {
      actions.saveData(queryTarif.data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTarif.isSuccess, queryTarif.isFetching]);

  if (queryTarif.isFetching && !queryTarif.isFetchedAfterMount) {
    return <LoaderScreen />;
  }

  if (queryTarif.isError || state.Tarif === undefined) {
    // @ts-expect-error - error type from axios does not expose response.data property
    const errorMessage = queryTarif.error?.response?.data?.error ?? 'Erreur lors de la récupération des tarifs';

    return (
      <ErrorTarification
        errorMessage={errorMessage}
      />
    );
  }

  return <Tarification />;
}

export function ErrorTarification({ errorMessage }: { errorMessage: string }) {
  const {
    actions: { goPreviousQuestion },
  } = useGlobalForm();
  const {isConseiller} = useSource()

  const handleReload = () => {
    window.location.reload();
    // removeCookie('devis_token');
    localStorage.clear();
  };

  return (
    <div className="mx-auto px-6 py-8 bg-card text-center rounded-xl mt-10">
      <XMarkIcon
        className="h-16 w-16 text-red-500 mx-auto"
        strokeWidth={1}
      />
      <h2 className="text-xl font-medium text-secondary mt-2">Les informations que vous avez saisies ne nous permettent pas de vous proposer un tarif</h2>
      <p className="text-gray-500 text-sm mt-2">Si c'est une erreur de saisie, vous pouvez modifier les informations déclarées sur les étapes précédentes.</p>

      {isConseiller && <p className="text-red-500 text-sm mt-2">({errorMessage})</p>}

      <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
        <Button
          onClick={goPreviousQuestion}
          size="sm"
          variant="outline"
          arrow={false}
        >
          Retour
        </Button>

        <Button
          onClick={handleReload}
          size="sm"
          variant="outline"
          arrow={false}
        >
          Recommencer le devis
        </Button>
      </div>
    </div>
  );
}

export function Tarification() {
  const methods = useSmartFormGlobal<FormSchemaType>();
  const Tarif = methods.watch('Tarif');
  const DemandeTarif = methods.watch('DemandeTarif');

  useEffect(() => {
    if (DemandeTarif.ListeVehicules.some((vehicule) => !vehicule.FormuleChoisie)) {
      methods.setValue(
        'DemandeTarif.ListeVehicules',
        DemandeTarif.ListeVehicules.map((vehicule) => ({ ...vehicule, FormuleChoisie: Tarif.FormulePreconisee })),
      );
    }
    if (DemandeTarif.ListeVehicules.some((vehicule) => !vehicule.ListeOptionsChoisies)) {
      methods.setValue(
        'DemandeTarif.ListeVehicules',
        DemandeTarif.ListeVehicules.map((vehicule) => ({ ...vehicule, ListeOptionsChoisies: [] })),
      );
    }

    if (!DemandeTarif.Police.FractionnementChoisi) {
      methods.setValue('DemandeTarif.Police.FractionnementChoisi', 'M');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DemandeTarif.ListeVehicules, Tarif.FormulePreconisee]);

  useEffect(() => {
    if (DemandeTarif.DevoirConseil.VersionRecueil === 1) {
      Tarif.ListeVehicules.forEach((vehicule) => {
        const formulePreconisee = vehicule.ListeFormules.find((formule) => formule.CodeFormule === vehicule.FormuleChoisie);
        if (formulePreconisee && DemandeTarif.ListeVehicules[vehicule.IdVehicule].ListeOptionsChoisies.length === 0) {
          formulePreconisee.ListeGaranties.forEach((garantie) => {
            if (garantie.TypeGarantie === 'Option' && garantie.Souscrite) {
              methods.setValue(
                'DemandeTarif.ListeVehicules',
                DemandeTarif.ListeVehicules.map((vehicule) => ({
                  ...vehicule,
                  ListeOptionsChoisies: [{ CodeOption: garantie.CodeGarantie }, ...(vehicule.ListeOptionsChoisies ?? [])],
                })),
              );
            }
          });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Tarif.ListeVehicules]);

  const formuleIsSet = DemandeTarif.ListeVehicules.every((vehicule) => vehicule.FormuleChoisie && vehicule.ListeOptionsChoisies);

  return (
    <SmartFormGlobal
      {...methods}
      className="mt-0 sm:mt-0 px-0 h-full mb-0 pb-20 max-w-none"
    >
      <div className="bg-primary w-full py-10 padding-root">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-2xl text-white">Choisissez votre formule pour assurer votre flotte</h1>
        </div>
      </div>

      <div className="lg:padding-root max-w-screen-xl w-full mx-auto space-y-16">
        {formuleIsSet && Tarif.ListeVehicules ? (
          <>
            <PricingTable />
            <Options />
            <Franchises />
            <FormuleByVehicule />
            <Commission config={{ showTauxApporteur: true, tauxApporteurLocked: false }} />
            <Souscrire />
            <p className="text-gray-500 mt-12 text-xs padding-root">Tous les prix affichés sont en TTC.</p>
          </>
        ) : (
          <div className="flex justify-center items-center h-full">
            <LoaderScreen />
          </div>
        )}
      </div>
    </SmartFormGlobal>
  );
}

interface PricingTableProps {
  Formules: any[];
  Vehicules: any[];
  selectFormuleForAllVehicules: (formule: string) => void;
}

function PricingTable() {
  const methods = useFormContext<FormSchemaType>();

  const Tarif = methods.watch('Tarif');
  const Vehicules = methods.watch('DemandeTarif.ListeVehicules');
  const TarifVehicules = Tarif.ListeVehicules;
  // additionne les prix mensuels de chaque véhicule pour avoir le prix total pour chaque formule
  let Formules = TarifVehicules[0].ListeFormules.map((formule, formuleIndex: number) => {
    const TotalFormulePrime = TarifVehicules.reduce(
      (
        acc: {
          TotalPrimeMensuelle: number;
          TotalPrimeAnnuelle: number;
        },
        vehicule,
      ) => {
        const formuleVehicule = vehicule.ListeFormules[formuleIndex];
        const primeMensuelle = formuleVehicule.PrimeMensuelleBase;
        const primeAnnuelle = formuleVehicule.PrimeAnnuelleFracAnnuelBase;

        return {
          TotalPrimeMensuelle: acc.TotalPrimeMensuelle + primeMensuelle,
          TotalPrimeAnnuelle: acc.TotalPrimeAnnuelle + primeAnnuelle,
        };
      },
      {
        TotalPrimeMensuelle: 0,
        TotalPrimeAnnuelle: 0,
      },
    );
    const ListeGaranties = formule.ListeGaranties.filter((garantie) => garantie.TypeGarantie === 'Principale').sort(
      (a, b) => a.OrdreAffichage - b.OrdreAffichage,
    );

    const TotalSelected = Vehicules.reduce((acc: number, vehicule) => {
      if (vehicule.FormuleChoisie === formule.CodeFormule) {
        return acc + 1;
      }
      return acc;
    }, 0);

    return {
      ...formule,
      ...TotalFormulePrime,
      ListeGaranties,
      TotalSelected,
    };
  }) as any;

  Formules = Formules.map((formule: any, formuleIndex: number) => {
    const nextFormule = formuleIndex + 1 < Formules.length ? Formules[formuleIndex + 1] : null;

    formule.ListeGaranties = formule.ListeGaranties.map((garantie: { Disponible: string }, garantieIdx: number) => {
      return {
        ...garantie,
        isIncluded: garantie.Disponible === 'Inclusion',
        // nextGarantieIsIncluded: garantieIdx < ListeGaranties.length ? ListeGaranties[garantieIdx].Disponible === 'Inclusion' : false,
        nextGarantieIsIncluded: nextFormule ? nextFormule.ListeGaranties[garantieIdx].Disponible === 'Inclusion' : false,
      };
    });

    return {
      ...formule,
      isSelected: formule.TotalSelected > 0,
      nextFormuleIsSelected: formuleIndex + 1 < Formules.length ? Formules[formuleIndex + 1].TotalSelected > 0 : false,
    };
  });

  const selectFormuleForAllVehicules = (formule: string) => {
    methods.setValue(
      'DemandeTarif.ListeVehicules',
      Vehicules.map((vehicule) => ({ ...vehicule, FormuleChoisie: formule })),
    );
  };

  return (
    <div>
      <PricingTableMobile
        Formules={Formules}
        Vehicules={Vehicules}
        selectFormuleForAllVehicules={selectFormuleForAllVehicules}
      />
      <PricingTableDesktop
        Formules={Formules}
        Vehicules={Vehicules}
        selectFormuleForAllVehicules={selectFormuleForAllVehicules}
      />
    </div>
  );
}

function PricingTableMobile({ Formules, Vehicules, selectFormuleForAllVehicules }: PricingTableProps) {
  const { watch } = useFormContext<FormSchemaType>();
  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');

  return (
    <section className="lg:hidden flow-root">
      <div className="inline-block min-w-full py-2">
        <div className="bg-white w-full">
          <div className="grid grid-cols-3 sticky top-0  z-20">
            {Formules.map((formule, formuleId) => (
              <div
                className={cn(
                  'cursor-pointer py-2 px-1 bg-white border bg-opacity-50 backdrop-blur backdrop-filter text-center',
                  formule.isSelected && 'bg-card border-primary',
                )}
                key={formuleId}
                onClick={() => {
                  selectFormuleForAllVehicules(formule.CodeFormule);
                }}
              >
                <p className="text-sm font-semibold uppercase">{formule.LibelleFormule}</p>
                <span className="text-sm font-semibold text-primary mt-1 block">
                  {normalizeEuro(FractionnementChoisi === 'M' ? formule.TotalPrimeMensuelle : formule.TotalPrimeAnnuelle)}
                </span>
                <span className="text-xs font-normal text-gray-500 mt-0.5 block">{FractionnementChoisi === 'M' ? '/mois' : '/an'}</span>
              </div>
            ))}
          </div>

          <div>
            {Formules[0].ListeGaranties.map((garantie: any, garantieIdx: number) => (
              <Fragment key={garantieIdx}>
                <div data-code={garantie.CodeGarantie}>
                  <div className="py-3 pr-4 pl-4 text-left bg-white border-x align-middle leading-none text-sm relative">
                    <Disclosure>
                      {({ open }) => (
                        <>
                          <DisclosureButton className="absolute inset-0"></DisclosureButton>
                          <div className="flex items-center justify-between w-full">
                            <h3>{garantie.LibelleGarantie}</h3>
                            <ChevronRightIcon
                              aria-hidden="true"
                              className={cn('h-4 w-4', open ? 'rotate-90 transform' : '')}
                            />
                          </div>

                          <DisclosurePanel>
                            <p className="text-xs text-zinc-400 font-light mt-1 leading-snug">
                              {garanties[garantie.CodeGarantie] ? garanties[garantie.CodeGarantie].description : null}
                            </p>
                          </DisclosurePanel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                </div>

                <div
                  className="grid grid-cols-3"
                  key={garantieIdx}
                >
                  {Formules.map((formule) => {
                    const currentGarantie = formule.ListeGaranties[garantieIdx];

                    return (
                      <div
                        className={cn(
                          'bg-white border-r border-y h-10 flex items-center justify-center',
                          formule.nextFormuleIsSelected && currentGarantie.nextGarantieIsIncluded && 'border-r-primary',
                          garantieIdx === Formules[0].ListeGaranties.length - 1 && 'border-b-0',
                          formule.isSelected && currentGarantie.isIncluded && 'bg-card border-primary',
                        )}
                        key={`${formule.CodeFormule}-${currentGarantie.CodeGarantie}`}
                      >
                        {currentGarantie.isIncluded ? (
                          <CheckIcon
                            aria-hidden="true"
                            className="mx-auto h-5 w-5 text-primary"
                          />
                        ) : (
                          <XMarkIconMini
                            aria-hidden="true"
                            className="mx-auto h-5 w-5 text-gray-500"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            ))}
          </div>
          <div className="grid grid-cols-3 sticky bottom-0 z-20">
            {Formules.map((formule, formuleIdx) => (
              <div
                className={cn(
                  'bg-white border-r border-y p-2 sticky bottom-0 inline-flex items-center justify-center',
                  formule.isSelected && 'bg-card border-primary',
                  formule.nextFormuleIsSelected && 'border-r-primary',
                )}
                key={formuleIdx}
              >
                <Button
                  arrow={false}
                  onClick={() => {
                    selectFormuleForAllVehicules(formule.CodeFormule);
                  }}
                  size="xs"
                  variant={formule.isSelected ? 'outline' : 'primary'}
                >
                  {formule.TotalSelected > 0 ? (
                    <>
                      <CheckIcon
                        aria-hidden="true"
                        className="h-4 w-4 text-primary"
                      />
                      {formule.TotalSelected} / {Vehicules.length}
                    </>
                  ) : (
                    'Choisir'
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingTableDesktop({ Formules, Vehicules, selectFormuleForAllVehicules }: PricingTableProps) {
  const { watch } = useFormContext<FormSchemaType>();
  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');
  const FormulePreconisee = watch('Tarif.FormulePreconisee');

  return (
    <section className="hidden lg:block">
      <table className="w-full border-collapse isolate border-spacing-0">
        <thead>
          <tr className="text-left">
            <th className="" />
            {Formules.map((formule, formuleId) => (
              <th
                className={cn(
                  'border pt-8 pb-4 px-2 text-center bg-white relative',
                  formule.isSelected && 'bg-card border-primary',
                  formule.nextFormuleIsSelected && 'border-r border-r-primary',
                )}
                key={formuleId}
                scope="col"
              >
                {FormulePreconisee === formule.CodeFormule && (
                  <span className="text-xs font-semibold bg-orange-600 text-white absolute -translate-x-1/2 px-4 py-1 rounded-b-md transform left-1/2 top-0 z-10">
                    Recommandée
                  </span>
                )}
                <p className="text-xl font-semibold uppercase">{formule.LibelleFormule}</p>
                <div className="mt-1">
                  <span className="text-xl font-semibold text-primary">
                    {normalizeEuro(FractionnementChoisi === 'M' ? formule.TotalPrimeMensuelle : formule.TotalPrimeAnnuelle)}
                  </span>
                  <span className="text-xs font-normal text-gray-500 ml-2">{FractionnementChoisi === 'M' ? '/mois' : '/an'}</span>
                </div>
                <div>
                  <span className="text-xs font-normal text-gray-500 mr-1">Soit</span>
                  <span className="text-sm font-semibold whitespace-nowrap">
                    {normalizeEuro(FractionnementChoisi === 'M' ? formule.TotalPrimeAnnuelle : formule.TotalPrimeMensuelle)}
                  </span>
                  <span className="text-xs font-normal text-gray-500 ml-1">{FractionnementChoisi === 'M' ? '/an' : '/mois'}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Formules[0].ListeGaranties.map((garantie: any, garantieIdx: number) => (
            <tr
              key={garantieIdx}
              data-code={garantie.CodeGarantie}
            >
              <td
                className={cn(
                  'w-2/5 py-6 pr-4 pl-4 text-left text-sm bg-white border align-middle leading-none relative',
                  Formules[0].isSelected && Formules[0].ListeGaranties[garantieIdx].isIncluded && 'border-r border-r-primary',
                  'hover:bg-card hover:text-primary transition-all',
                )}
                scope="row"
              >
                <Disclosure>
                  {({ open }) => (
                    <>
                      <DisclosureButton className="absolute inset-0"></DisclosureButton>

                      <div className="flex items-center justify-between w-full">
                        <h3>{garantie.LibelleGarantie}</h3>
                        <ChevronRightIcon
                          aria-hidden="true"
                          className={cn('h-4 w-4', open ? 'rotate-90 transform' : '')}
                        />
                      </div>
                      <DisclosurePanel>
                        <p className="text-xs text-zinc-500 font-light mt-1 leading-snug">
                          {garanties[garantie.CodeGarantie] ? garanties[garantie.CodeGarantie].description : null}
                        </p>
                      </DisclosurePanel>
                    </>
                  )}
                </Disclosure>
              </td>
              {Formules.map((formule) => {
                const currentGarantie = formule.ListeGaranties[garantieIdx];

                return (
                  <td
                    className={cn(
                      'w-1/5 bg-white border',
                      formule.isSelected && currentGarantie.isIncluded && 'bg-card border-primary',
                      formule.nextFormuleIsSelected && currentGarantie.nextGarantieIsIncluded && 'border-r border-r-primary',
                      formule.isSelected && garantieIdx === Formules[0].ListeGaranties.length - 1 && 'border-b border-b-primary',
                    )}
                    key={`${formule.CodeFormule}-${currentGarantie.CodeGarantie}`}
                  >
                    {currentGarantie.Disponible === 'Inclusion' ? (
                      <CheckIcon
                        aria-hidden="true"
                        className="mx-auto h-6 w-6 text-primary"
                      />
                    ) : (
                      <XMarkIconMini
                        aria-hidden="true"
                        className="mx-auto h-6 w-6 text-gray-500"
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="text-left">
            <th
              className="sticky bottom-0 z-10"
              scope="col"
            />
            {Formules.map((formule, formuleIdx) => (
              <th
                className={cn(
                  'sticky bottom-0 z-10 bg-white border text-center py-4 px-2',
                  formule.isSelected && 'bg-card border-primary',
                  formule.nextFormuleIsSelected && 'border-r border-r-primary',
                )}
                key={formuleIdx}
              >
                <Button
                  arrow={false}
                  onClick={() => {
                    selectFormuleForAllVehicules(formule.CodeFormule);
                  }}
                  size="xs"
                  variant={formule.isSelected ? 'outline' : 'primary'}
                >
                  {formule.isSelected ? (
                    <>
                      <CheckIcon
                        aria-hidden="true"
                        className="h-4 w-4 text-primary"
                      />
                      {`${formule.TotalSelected} / ${Vehicules.length} véhicule${formule.TotalSelected > 1 ? 's' : ''}`}
                    </>
                  ) : (
                    'Sélectionnez pour ma flotte'
                  )}
                </Button>
              </th>
            ))}
          </tr>
        </tfoot>
      </table>
    </section>
  );
}

const options = {
  ASS1: {
    icon: Depanneuse,
    description: "Assistance Panne 0km. Panne, crevaison, perte ou vol des clés, erreur de carburant,accident, voyage à l'étranger...",
  },
  ASS1TSCA: {
    icon: Depanneuse,
    description: "Assistance Panne 0km. Panne, crevaison, perte ou vol des clés, erreur de carburant,accident, voyage à l'étranger...",
  },
  ACCESSEQP: {
    icon: Casques,
    description: 'Sans eux, vous ne pouvez pas prendre la route. Pensez à assurer vos accessoires et équipements.',
  },
  PCC1: {
    icon: Protection,
    description:
      "La Protection corporelle du conducteur vous indemnise : en cas d'invalidité (≥15%) : jusqu'à 150 000 en cas de dèces : 15 000 € pour votre conjoint et 3 000 € par enfant",
  },
};

function Options() {
  const methods = useFormContext<FormSchemaType>();
  const Vehicules = methods.watch('DemandeTarif.ListeVehicules');
  const Options = methods
    .watch('Tarif.ListeVehicules.0.ListeFormules.0.ListeGaranties')
    .filter((garantie) => garantie.TypeGarantie === 'Option')
    .sort((a, b) => a.OrdreAffichage - b.OrdreAffichage);
  const FractionnementChoisi = methods.watch('DemandeTarif.Police.FractionnementChoisi');

  const OptionsMap = Options.map((option: any) => {
    const howManyVehicules = Vehicules.filter((vehicule: any) =>
      vehicule.ListeOptionsChoisies.map((option: any) => option.CodeOption).includes(option.CodeGarantie),
    ).length;
    const hasVehicule = howManyVehicules > 0;
    const TotalPrimeMensuelle = Vehicules.length * option.PrimeClientMensuelle;
    const TotalPrimeAnnuelle = Vehicules.length * option.PrimeClient;

    return {
      ...option,
      TotalPrimeMensuelle,
      TotalPrimeAnnuelle,
      hasVehicule,
      howManyVehicules,
    };
  });

  const addGarantieToAllVehicules = (garantie: string) => {
    const ListeVehicules = Vehicules.map((vehicule: any) => {
      if (vehicule.ListeOptionsChoisies.map((option: any) => option.CodeOption).includes(garantie)) {
        return vehicule;
      }
      return {
        ...vehicule,
        ListeOptionsChoisies: [{ CodeOption: garantie }, ...vehicule.ListeOptionsChoisies].filter(
          (option, index: number, self) => self.findIndex((t) => t.CodeOption === option.CodeOption) === index,
        ),
      };
    });

    methods.setValue('DemandeTarif.ListeVehicules', ListeVehicules);
  };

  const removeGarantieToAllVehicules = (garantie: string) => {
    const ListeVehicules = Vehicules.map((vehicule: any) => {
      if (!vehicule.ListeOptionsChoisies.map((option: any) => option.CodeOption).includes(garantie)) {
        return vehicule;
      }
      return {
        ...vehicule,
        ListeOptionsChoisies: vehicule.ListeOptionsChoisies.filter((option: any) => option.CodeOption !== garantie),
      };
    });

    methods.setValue('DemandeTarif.ListeVehicules', ListeVehicules);
  };

  return (
    <div className="padding-root lg:px-0">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-secondary">Compléter votre couverture avec des options sur-mesure</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {OptionsMap.map((option: any, optionIdx: number) => {
          const Icon = options[option.CodeGarantie as 'ASS1' | 'ASS1TSCA' | 'ACCESSEQP' | 'PCC1'].icon ?? undefined;

          return (
            <div
              className={cn('relative p-4 flex flex-col gap-5 rounded-md border transition-all', option.hasVehicule ? 'border-primary bg-white' : 'bg-white')}
              key={optionIdx}
            >
              <div className={cn('absolute inset-0 rounded-md z-0', option.hasVehicule && 'bg-card opacity-50')} />
              <div className="flex-1 z-10 text-center">
                <Icon className="h-10 w-10 mx-auto" />
                <h3 className="text-base font-medium text-primary">{option.LibelleGarantie}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{option.ConditionsIndemnisation}</p>
              </div>

              <div className="flex items-center gap-2 z-10">
                {option.hasVehicule ? (
                  <Button
                    arrow={false}
                    className="w-full"
                    onClick={() => {
                      removeGarantieToAllVehicules(option.CodeGarantie);
                    }}
                    size="xs"
                    variant="neutral"
                  >
                    Enlever de ma flotte
                  </Button>
                ) : null}
                <Button
                  arrow={false}
                  className="w-full"
                  onClick={() => {
                    addGarantieToAllVehicules(option.CodeGarantie);
                  }}
                  size="xs"
                  variant={option.hasVehicule ? 'outline' : 'primary'}
                >
                  {option.hasVehicule ? (
                    <>
                      <CheckIcon
                        aria-hidden="true"
                        className="h-4 w-4 text-primary"
                      />
                      {`${option.howManyVehicules} / ${Vehicules.length} véhicule${option.howManyVehicules > 1 ? 's' : ''}`}
                    </>
                  ) : (
                    `Ajouter à ma flotte pour ${
                      FractionnementChoisi === 'M' ? `${normalizeEuro(option.TotalPrimeMensuelle)}/mois` : `${normalizeEuro(option.TotalPrimeAnnuelle)}/an`
                    }`
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Franchises() {
  const methods = useFormContext<FormSchemaType>();

  const onlyTheseGaranties = ['VOL', 'DTA', 'INC'];

  const Garanties = methods.watch('Tarif.ListeVehicules.0.ListeFormules.0.ListeGaranties');
  const FilteredGaranties = Garanties.filter((garantie: any) => onlyTheseGaranties.includes(garantie.CodeGarantie)).sort(
    (a, b) => a.OrdreAffichage - b.OrdreAffichage,
  );

  return (
    <div className="padding-root lg:px-0">
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-secondary">Détail de vos franchises</h2>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {FilteredGaranties.map((garantie: any, garantieIdx: number) => (
          <div
            className="bg-white border rounded-md p-4"
            key={garantieIdx}
          >
            <h3 className="text-base font-medium">{garantie.LibelleGarantie}</h3>
            <p className="text-sm mt-1 leading-relaxed">
              {garantie.ConditionsIndemnisation}
              <span className="text-xs text-gray-500 ml-2">par sinistre</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormuleByVehicule() {
  return (
    <div>
      <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-secondary padding-root lg:px-0 mb-4 sm:mb-6">Détail des garanties par véhicule</h2>

      <FormuleByVehiculeDesktop />
      <FormuleByVehiculeMobile />
    </div>
  );
}

function FormuleByVehiculeDesktop() {
  const { actions } = useGlobalForm();
  const { watch } = useFormContext<FormSchemaType>();
  const { Formules, Options, VehiculesMap } = useTarifFormatedData();

  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');
  return (
    <table className="w-full border-collapse hidden lg:table">
      <thead>
        <tr>
          <th />
          <th
            className="bg-white border py-2 font-medium"
            colSpan={Formules.length}
          >
            Vos formules
          </th>
          <th
            className="bg-white border py-2 font-medium border-l-4"
            colSpan={Options.length}
          >
            Vos options
          </th>
          <th />
        </tr>

        <tr className="text-xs">
          <th className="text-left px-4 pb-5 align-bottom">
            <span className="font-normal text-left">
              Pour modifier vos véhicules,{' '}
              <em
                className="hover:underline cursor-pointer"
                onClick={() => {
                  actions.updateStep(getStepBySlug('vehicules', steps).index);
                }}
              >
                cliquez ici
              </em>
              .
            </span>
          </th>
          {Formules.map((formule, index) => (
            <th
              className="text-center bg-white border w-24 xl:w-32 py-2 px-1 relative"
              key={index}
            >
              <span className="block text-xs font-medium">{formule.LibelleFormule}</span>
            </th>
          ))}
          {Options.map((option, index) => (
            <th
              className={cn('text-center bg-white border w-28 xl:w-36 py-2 px-1 font-normal', index === 0 && 'border-l-4')}
              key={index}
            >
              <span className="block text-xs font-medium">{option.LibelleGarantie}</span>
            </th>
          ))}
          <th className="bg-white border w-28 font-normal">
            <span className="text-sm font-medium text-secondary">Total</span>
            <span className="text-xs text-gray-500 ml-1">{FractionnementChoisi === 'M' ? '/mois' : '/an'}</span>
          </th>
        </tr>
      </thead>

      <tbody>
        {VehiculesMap.map((vehicule, vehiculeIndex) => (
          <tr
            className="bg-white text-center"
            key={vehiculeIndex}
          >
            <td className="border">
              <div className="py-3 px-4 text-left">
                <h3 className="text-base font-medium text-secondary">
                  {vehicule.Marque} {vehicule.Modele} - {vehicule.Immatriculation}
                </h3>
                <p className="text-xs text-gray-500">Mise en circulation le {vehicule.DateMEC}</p>
              </div>
            </td>
            <VehiculeFormules
              vehicule={vehicule}
              vehiculeIndex={vehiculeIndex}
            />
            <VehiculeOptions
              vehicule={vehicule}
              vehiculeIndex={vehiculeIndex}
            />

            <td className="border px-3">
              <div className="">
                <span className="text-base font-semibold text-primary">
                  {normalizeEuro(FractionnementChoisi === 'M' ? vehicule.totalPriceVehiculeMensuel : vehicule.totalPriceVehiculeAnnuel)}
                </span>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FormuleByVehiculeMobile() {
  const { watch } = useFormContext<FormSchemaType>();
  const { Formules, Options, VehiculesMap } = useTarifFormatedData();
  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');
  return (
    <table className="w-full border-collapse table-fixed table lg:hidden">
      <tbody>
        {VehiculesMap.map((vehicule, vehiculeIndex) => (
          <Fragment key={vehiculeIndex}>
            {vehiculeIndex !== 0 && <tr className="h-5" />}
            <tr
              className="bg-primary text-center"
              key={vehiculeIndex}
            >
              <td
                className="border"
                colSpan={3}
              >
                <div className="flex items-center justify-between py-3 px-4 text-white">
                  <div className="text-left">
                    <h3 className="text-base font-medium">
                      {vehicule.Marque} {vehicule.Modele} - {vehicule.Immatriculation}
                    </h3>
                    <p className="text-xs">Mise en circulation le {vehicule.DateMEC}</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-xs font-semibold">Total</span>
                    <span className="block text-sm font-medium">
                      {normalizeEuro(FractionnementChoisi === 'M' ? vehicule.totalPriceVehiculeMensuel : vehicule.totalPriceVehiculeAnnuel)}
                    </span>
                    <span className="block text-xs font-normal">{FractionnementChoisi === 'M' ? '/mois' : '/an'}</span>
                  </div>
                </div>
              </td>
            </tr>

            <tr className="h-10">
              {Formules.map((formule, formuleIndex) => (
                <td
                  className="text-center bg-white border py-2 px-1 relative"
                  key={formuleIndex}
                >
                  <span className="block text-xs font-medium">{formule.LibelleFormule}</span>
                </td>
              ))}
            </tr>
            <tr className="h-14">
              <VehiculeFormules
                vehicule={vehicule}
                vehiculeIndex={vehiculeIndex}
              />
            </tr>

            <tr className="h-16">
              {Options.map((option) => (
                <td
                  className="text-center bg-white border py-2 px-1 font-normal"
                  key={option.CodeGarantie}
                >
                  <span className="block text-xs font-medium">{option.LibelleGarantie}</span>
                </td>
              ))}
            </tr>
            <tr className="h-14">
              <VehiculeOptions
                vehicule={vehicule}
                vehiculeIndex={vehiculeIndex}
              />
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

interface VehicleFormulesProps {
  vehicule: any;
  vehiculeIndex: number;
}

function VehiculeFormules({ vehicule, vehiculeIndex }: VehicleFormulesProps) {
  const { watch, setValue } = useFormContext<FormSchemaType>();
  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');
  const formuleChoisieIndex = vehicule.VehiculeListeFormules.findIndex((formule: any) => formule.CodeFormule === vehicule.FormuleChoisie);
  const formuleChoisie = vehicule.VehiculeListeFormules[formuleChoisieIndex];

  return vehicule.VehiculeListeFormules.map((formule: any, index: number) => {
    const isFormuleSelected = index === formuleChoisieIndex;
    const prixActuel = FractionnementChoisi === 'M' ? formuleChoisie.PrimeMensuelleBase : formuleChoisie.PrimeAnnuelleFracAnnuelBase;
    let texteDifferencePrix = '';
    let differencePrix = 0;
    const prixFormule = FractionnementChoisi === 'M' ? formule.PrimeMensuelleBase : formule.PrimeAnnuelleFracAnnuelBase;

    if (isFormuleSelected) {
      texteDifferencePrix = normalizeEuro(prixActuel);
    } else {
      differencePrix = prixFormule - prixActuel;

      texteDifferencePrix = differencePrix > 0 ? `+${normalizeEuro(differencePrix)}` : normalizeEuro(differencePrix);
    }

    return (
      <td
        className={cn(
          'border cursor-pointer transition-all group relative align-middle text-center',
          isFormuleSelected
            ? 'bg-card text-primary'
            : differencePrix > 0
              ? 'bg-white text-gray-500 hover:bg-green-100 hover:text-green-500'
              : 'bg-white text-gray-500 hover:bg-red-100 hover:text-red-500',
        )}
        key={index}
        onClick={() => {
          setValue(`DemandeTarif.ListeVehicules.${vehiculeIndex}.FormuleChoisie`, formule.CodeFormule);
        }}
      >
        <div className="flex items-center justify-center gap-2 h-full">
          <span className={cn('block text-xs font-medium', isFormuleSelected ? 'opacity-100 ' : 'group-hover:absolute group-hover:opacity-0')}>
            {normalizeEuro(prixFormule)}
          </span>
          <div
            className={cn(
              'text-xs font-medium flex flex-col',
              isFormuleSelected ? 'opacity-0 absolute' : 'absolute group-hover:inline-flex opacity-0 group-hover:opacity-100',
            )}
          >
            <span>Modifier</span>
            <span>({texteDifferencePrix})</span>
          </div>
          {isFormuleSelected ? (
            <CheckIcon
              aria-hidden="true"
              className="h-5"
            />
          ) : null}
        </div>
      </td>
    );
  });
}

function VehiculeOptions({ vehicule, vehiculeIndex }: VehicleFormulesProps) {
  const { watch, setValue } = useFormContext<FormSchemaType>();
  const optionsValue = vehicule.selectedOptions.map((option: any) => option.CodeGarantie);
  const FractionnementChoisi = watch('DemandeTarif.Police.FractionnementChoisi');

  const addOptionToVehicule = (option: string) => {
    const ListeOptionsChoisies = vehicule.ListeOptionsChoisies;
    const optionIndex = ListeOptionsChoisies.findIndex((opt: any) => opt.CodeOption === option);

    if (optionIndex !== -1) {
      ListeOptionsChoisies.splice(optionIndex, 1);
    } else {
      ListeOptionsChoisies.push({ CodeOption: option });
    }

    setValue(`DemandeTarif.ListeVehicules.${vehiculeIndex}.ListeOptionsChoisies`, ListeOptionsChoisies);
  };

  return vehicule.VehiculeListeOptions.map((option: any, index: number) => {
    const isSelected = optionsValue?.includes(option.CodeGarantie);

    return (
      <td
        className={cn(
          'border cursor-pointer transition-all group relative align-middle px-1',
          isSelected ? 'bg-card text-primary hover:bg-red-100 hover:text-red-500' : 'bg-white text-gray-500 hover:bg-green-100 hover:text-green-500',
          index === 0 && 'border-l-4',
        )}
        key={index}
        onClick={() => {
          addOptionToVehicule(option.CodeGarantie);
        }}
      >
        <div className="w-full h-full flex items-center justify-center gap-2">
          <span className={cn('font-medium absolute text-xs', isSelected ? 'opacity-0 group-hover:opacity-100' : 'opacity-0 group-hover:opacity-0')}>
            Enlever (-{FractionnementChoisi === 'M' ? normalizeEuro(option.PrimeClientMensuelle) : normalizeEuro(option.PrimeClient)})
          </span>

          <span className={cn('block text-xs font-medium absolute', isSelected ? 'opacity-0' : '')}>
            Ajouter (+{FractionnementChoisi === 'M' ? normalizeEuro(option.PrimeClientMensuelle) : normalizeEuro(option.PrimeClient)})
          </span>

          <div className={cn('w-full h-full flex items-center justify-center gap-2', isSelected ? 'opacity-100 group-hover:opacity-0' : 'opacity-0 ')}>
            <span className="text-xs font-medium">{normalizeEuro(FractionnementChoisi === 'M' ? option.PrimeClientMensuelle : option.PrimeClient)}</span>
            <CheckIcon
              aria-hidden="true"
              className={cn('h-5')}
            />
          </div>
        </div>
      </td>
    );
  });
}

function Souscrire() {
  const { meta, actions } = useGlobalForm();
  const methods = useFormContext<FormSchemaType>();
  const { totalPrimeMensuelleWithFrais, totalPrimeAnnuelleWithFrais, totalAttentat } = useTarifFormatedData();
  const mutationFill = useMutationFill(meta.token);
  const isAvenant = methods.watch('DemandeTarif.ActeGestion') === 'AV';
  const source = methods.watch('source');
  const urlDocumentInformation =
    methods.watch('Tarif.UrlPDFDocumentInformation') || methods.watch('Devis.UrlPDFDocumentInformation');

  const handleSubscription = async () => {
    const data = methods.getValues();

    const response = await mutationFill.mutateAsync({
      ...data,
      DemandeDevis: { ...data.DemandeDevis, OptinRecontact: false },
    });

    actions.saveAndGoNextStep(response?.data);
  };

  const handleOptinRecontact = async () => {
    const data = methods.getValues();

    const response = await mutationFill.mutateAsync({
      ...data,
      DemandeDevis: { ...data.DemandeDevis, OptinRecontact: true },
    });

    actions.saveAndGoNextStep(response?.data);
  };

  // DemandeDevis.OptinRecontact : true

  const Vehicules = methods.watch('DemandeTarif.ListeVehicules');
  const FractionnementChoisi = methods.watch('DemandeTarif.Police.FractionnementChoisi');

  const MontantFraisDossier = FractionnementChoisi === 'M' ? methods.watch('Tarif.MontantFraisDossier') / 12 : methods.watch('Tarif.MontantFraisDossier');
  const totalAttentatFractionnement = FractionnementChoisi === 'M' ? totalAttentat / 12 : totalAttentat;

  return (
    <div className="w-full max-w-screen-xl mx-auto">
      <div className="bg-white border border-border w-full lg:rounded-md padding-root lg:px-10 py-8 flex flex-col justify-between md:flex-row gap-6 lg:gap-8 items-center">
        <div className="flex flex-row items-center md:items-start xl:items-center md:flex-col justify-between xl:flex-row w-full gap-2 xl:gap-8">
          <div>
            <h2 className="text-sm sm:text-lg text-secondary">Votre formule</h2>
            <h3 className="text-xl sm:text-2xl font-semibold whitespace-nowrap">
              Flotte de {Vehicules.length} véhicule{Vehicules.length > 1 && 's'}
            </h3>
            <p className="text-sm text-gray-500">Avec vos options</p>
          </div>

          <div className="lg:w-52 text-right md:text-left">
            <div>
              <span className="text-xl sm:text-2xl text-primary font-medium">
                {normalizeEuro(FractionnementChoisi === 'M' ? totalPrimeMensuelleWithFrais : totalPrimeAnnuelleWithFrais)}
              </span>
              <span className="text-sm">{FractionnementChoisi === 'M' ? '/mois' : '/an'}*</span>
            </div>
            <p className="text-[10px] lg:text-xs text-gray-500">
              *Prix TTC {MontantFraisDossier > 0 && `incluant ${normalizeEuro(MontantFraisDossier)} de frais`}{' '}
              {totalAttentatFractionnement > 0 && `et ${normalizeEuro(totalAttentatFractionnement)} de taxe attentat`}
            </p>
          </div>
        </div>

        {!isAvenant && (
          <div className="flex flex-col gap-2 w-full">
            <p className="text-xs text-secondary font-medium">Je souhaite régler en :</p>
            <div className="flex items-stretch text-center">
              <button
                className={cn(
                  FractionnementChoisi === 'M' ? 'bg-card border-primary' : 'border-border',
                  FractionnementChoisi === 'A' && 'border-r border-r-primary',
                  'transition-all w-1/2 lg:w-40 border-l border-y px-10 py-1.5 flex flex-col items-center rounded-l-xl',
                )}
                onClick={() => {
                  methods.setValue('DemandeTarif.Police.FractionnementChoisi', 'M');
                }}
                type="button"
              >
                <span className="text-xs font-medium text-primary">Mensuel</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">{normalizeEuro(totalPrimeMensuelleWithFrais)}/mois</span>
              </button>
              <span className="bg-primary w-[0.5px]" />
              <button
                className={cn(
                  FractionnementChoisi === 'A' ? 'bg-card border-primary' : 'border-border',
                  'transition-all border-r border-y w-1/2 lg:w-40 py-1.5 flex flex-col items-center rounded-r-xl',
                )}
                onClick={() => {
                  methods.setValue('DemandeTarif.Police.FractionnementChoisi', 'A');
                }}
                type="button"
              >
                <span className="text-xs font-medium text-secondary">Annuel</span>
                <span className="text-xs text-gray-500 whitespace-nowrap">{normalizeEuro(totalPrimeAnnuelleWithFrais)}/an</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-col lg:flex-col gap-2 w-full lg:max-w-72">
          <Button
            className="w-full px-3 xl:px-6"
            loading={mutationFill.isPending}
            onClick={handleSubscription}
            size="xs"
            variant="primary"
          >
            Souscrire
          </Button>
          <Button
            arrow={false}
            className="w-full"
            loading={mutationFill.isPending}
            onClick={handleOptinRecontact}
            size="xs"
            variant="neutral"
          >
            {source === 'courtier' ? 'Obtenir la proposition' : 'Recevoir ma proposition par email'}
          </Button>
        </div>
      </div>

      {urlDocumentInformation && (
        <p className="text-sm text-center mt-4 padding-root">
          <a className="text-primary underline hover:no-underline" href={urlDocumentInformation} rel="noopener noreferrer" target="_blank">
            Veuillez trouver ci-joint le document d’information sur le produit d’assurance proposé.
          </a>
        </p>
      )}
    </div>
  );
}
